import { Customer } from '@/types/Customer';
import { PRICE_LIST } from './priceList';
import { AreaPriceDetails, CalculatedPriceObject, PriceDetails } from './types';

export const FLOW_FEE_FOOTNOTE =
  'Flödesavgift betalas under januari–april och oktober–december.';

export const normalizeProductCode = (productCode: string): string =>
  productCode.toLowerCase().replace(/\s/g, '');

export const getProductPrices = (
  productNumber: string | undefined | null,
  prices: AreaPriceDetails = PRICE_LIST,
): PriceDetails | undefined => {
  if (!productNumber) {
    return undefined;
  }
  const normalizedProductNumber = normalizeProductCode(productNumber);
  const productKey = Object.keys(prices).find(
    (key) => normalizeProductCode(key) === normalizedProductNumber,
  );
  return productKey ? prices[productKey] : undefined;
};

export const getDisplayYears = (
  prices: PriceDetails | undefined,
  selectedYear: number,
  selectedMonth: number,
): number[] => {
  if (!prices) {
    return [];
  }
  const priceYears = Object.keys(prices).map(Number);
  if (priceYears.length === 0) {
    return [];
  }
  const latestYear = Math.max(...priceYears);
  if (selectedYear > latestYear) {
    return [latestYear];
  }
  if (selectedYear === latestYear && selectedMonth >= 2) {
    return [latestYear];
  }
  return [latestYear - 1, latestYear].filter((year) => prices[year]);
};

export const hideFjvPriceCalculator = (customer: Customer | null): boolean => {
  if (!customer || !customer.facilities) {
    return true;
  }
  const hasFjvProduct = customer.facilities.some((facility) =>
    facility.contracts?.some(
      (contract) => getProductPrices(contract.productNumber) !== undefined,
    ),
  );
  return !hasFjvProduct;
};

export const calculateCosts = (
  year: number,
  month: number,
  flow: number,
  consumption: number,
  productId: string,
  prices: AreaPriceDetails,
): CalculatedPriceObject | undefined => {
  const selectedPrice = getProductPrices(productId, prices)?.[year]?.[month];
  if (!selectedPrice) {
    return undefined;
  }
  const fixedFee = selectedPrice.fixedFee;
  const consumptionFee = selectedPrice.consumptionFee * consumption;
  const flowFee =
    selectedPrice.flowFee == null ? undefined : selectedPrice.flowFee * flow;
  const subtotal = fixedFee + consumptionFee + (flowFee ?? 0);
  const vat = subtotal * 0.25;
  const total = subtotal + vat;
  return {
    total,
    fixedFee,
    consumptionFee,
    flowFee,
    vat,
  };
};
